// In-Memory-Clipboard (Module-Scope, kein OS-Clipboard).
// Copy/Cut/Paste/Duplicate fuer ausgewaehlte Objekte + dazwischen liegende Links.
// IDs werden beim Paste neu vergeben; Cascade-Referenzen (parentRegionId, blockId)
// und Link-from/to werden auf die neuen IDs gemappt damit Method-Scaffolds und
// interne Links nach Paste konsistent bleiben.

import { type CanvasObject, type Link, isCanvasObject, isLink } from "@ms-whiteboard/shared";
import type * as Y from "yjs";

interface ClipboardPayload {
  objects: CanvasObject[];
  links: Link[];
}

let clipboard: ClipboardPayload | null = null;

export function clipboardHasContent(): boolean {
  return clipboard !== null && clipboard.objects.length > 0;
}

/** Liest die ausgewaehlten Objekte + interne Links in den Module-Clipboard-Slot. */
export function copySelection(
  yMap: Y.Map<unknown>,
  yLinks: Y.Array<Link> | null,
  selection: Iterable<string>,
): boolean {
  const ids = new Set(selection);
  if (ids.size === 0) return false;
  const objects: CanvasObject[] = [];
  for (const id of ids) {
    const raw = yMap.get(id);
    if (isCanvasObject(raw)) objects.push(raw);
  }
  if (objects.length === 0) return false;
  const links: Link[] = [];
  if (yLinks) {
    for (const value of yLinks.toArray()) {
      if (!isLink(value)) continue;
      if (ids.has(value.from) && ids.has(value.to)) links.push(value);
    }
  }
  clipboard = { objects, links };
  return true;
}

export interface PasteOptions {
  /** Worldspace-Offset zur urspruenglichen Position. Default 20px nach SE. */
  offsetX?: number;
  offsetY?: number;
  /** Soll Paste die neuen IDs als Selection setzen? Caller bekommt die Liste retour. */
  newIds?: Set<string>;
}

export interface PasteResult {
  pastedObjectIds: string[];
  pastedLinkIds: string[];
}

export function pasteClipboard(
  yMap: Y.Map<unknown>,
  yLinks: Y.Array<Link> | null,
  userId: string,
  newId: () => string,
  opts: PasteOptions = {},
): PasteResult {
  if (!clipboard) return { pastedObjectIds: [], pastedLinkIds: [] };
  return pasteFromPayload(yMap, yLinks, userId, newId, clipboard, opts);
}

/** Variante fuer Duplicate: kopiert _ohne_ den Clipboard-Slot zu beruehren. */
export function duplicateSelection(
  yMap: Y.Map<unknown>,
  yLinks: Y.Array<Link> | null,
  selection: Iterable<string>,
  userId: string,
  newId: () => string,
  offset = 20,
): PasteResult {
  const ids = new Set(selection);
  if (ids.size === 0) return { pastedObjectIds: [], pastedLinkIds: [] };
  const objects: CanvasObject[] = [];
  for (const id of ids) {
    const raw = yMap.get(id);
    if (isCanvasObject(raw)) objects.push(raw);
  }
  if (objects.length === 0) return { pastedObjectIds: [], pastedLinkIds: [] };
  const links: Link[] = [];
  if (yLinks) {
    for (const value of yLinks.toArray()) {
      if (!isLink(value)) continue;
      if (ids.has(value.from) && ids.has(value.to)) links.push(value);
    }
  }
  return pasteFromPayload(
    yMap,
    yLinks,
    userId,
    newId,
    { objects, links },
    { offsetX: offset, offsetY: offset },
  );
}

function pasteFromPayload(
  yMap: Y.Map<unknown>,
  yLinks: Y.Array<Link> | null,
  userId: string,
  newId: () => string,
  payload: ClipboardPayload,
  opts: PasteOptions,
): PasteResult {
  const offsetX = opts.offsetX ?? 20;
  const offsetY = opts.offsetY ?? 20;
  const idMap = new Map<string, string>();
  for (const obj of payload.objects) idMap.set(obj.id, newId());

  const pastedObjectIds: string[] = [];
  const pastedLinkIds: string[] = [];

  const doc = yMap.doc;
  const op = (): void => {
    const now = Date.now();
    for (const obj of payload.objects) {
      const next = remapObject(obj, idMap, userId, now, offsetX, offsetY);
      yMap.set(next.id, next);
      pastedObjectIds.push(next.id);
    }
    if (yLinks) {
      for (const link of payload.links) {
        const from = idMap.get(link.from);
        const to = idMap.get(link.to);
        if (!from || !to) continue;
        const next: Link = {
          id: newId(),
          from,
          to,
          type: link.type,
          createdBy: userId,
          createdAt: now,
        };
        yLinks.push([next]);
        pastedLinkIds.push(next.id);
      }
    }
  };
  if (doc) doc.transact(op);
  else op();

  return { pastedObjectIds, pastedLinkIds };
}

function remapObject(
  obj: CanvasObject,
  idMap: Map<string, string>,
  userId: string,
  now: number,
  offsetX: number,
  offsetY: number,
): CanvasObject {
  const newId = idMap.get(obj.id);
  if (!newId) throw new Error(`paste: no id mapping for ${obj.id}`);
  const base = {
    ...obj,
    id: newId,
    x: obj.x + offsetX,
    y: obj.y + offsetY,
    createdBy: userId,
    createdAt: now,
  };
  // parentRegionId: wenn Region mit-kopiert wurde, remappen; sonst null setzen damit
  // gepastete Sticky nicht "fremde" Method-Scaffold-Mitgliedschaften erbt.
  if (base.parentRegionId && idMap.has(base.parentRegionId)) {
    base.parentRegionId = idMap.get(base.parentRegionId) ?? null;
  } else if (base.parentRegionId) {
    base.parentRegionId = null;
  }
  // blockId fuer Regions: nur remappen wenn Block mit-kopiert wurde, sonst stehen lassen
  // (Region ohne Block waere Schemaverletzung — Caller muss Region nicht ohne Block kopieren).
  if (base.type === "region") {
    const remappedBlock = idMap.get(base.blockId);
    if (remappedBlock) {
      return { ...base, blockId: remappedBlock };
    }
  }
  return base;
}
